/**
 * Administração → Consumo de IA.
 *
 * O que a instalação gastou com os provedores, em dólar, pelo preço da
 * época de cada chamada (`ai_usage_log`, com o preço gravado NA LINHA).
 * Só o Máster vê (decisão da dona, 2026-09-11): é custo que a
 * FSB paga, informação da instalação, não da empresa.
 *
 * **O dia é o da instalação, não o do navegador.** Os atalhos de
 * período contam a partir do hoje de Brasília (`installation-clock.ts`),
 * os dias viajam como `AAAA-MM-DD` e o servidor fecha o dia no mesmo
 * fuso — a resposta traz `timezone`, e a tela CONFERE contra o seu:
 * divergência vira aviso, não conta torta em silêncio.
 *
 * **As opções dos filtros vêm do resumo SEM filtro de dimensão** (só o
 * período), num pedido à parte quando há dimensão escolhida — mesma
 * regra do dashboard (2026-09-08): a resposta filtrada devolve só as
 * fatias que sobraram, e usá-la faria a lista de opções encolher a
 * cada escolha (marcar um modelo esconderia os outros).
 *
 * **Sem custo apurado nunca é zero.** Onde o custo falta, a tela diz
 * por quê — sem preço na época, sem token, não terminou — e quantas
 * chamadas do recorte ficaram de fora. A busca na web da Mestra Help tem
 * custo por requisição que o provedor não expõe no `usageMetadata`:
 * limitação declarada, marcada na linha da etapa ("+ busca não
 * apurada"), não silêncio.
 */

import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Coins,
  Cpu,
  Hash,
  ListChecks,
  MessageSquareOff,
  SlidersHorizontal
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { parseServerTime } from "@/features/uploads/upload-metrics";
import {
  apiClient,
  type AiUsageCall,
  type AiUsageCallsPage,
  type AiUsageQuery,
  type AiUsageStageSlice,
  type AiUsageSummary
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { EmptyState } from "@/shared/components/charts/EmptyState";
import { LineChart } from "@/shared/components/charts/LineChart";
import { MetricCard } from "@/shared/components/charts/MetricCard";
import { Panel } from "@/shared/components/charts/Panel";
import { RankedBars } from "@/shared/components/charts/RankedBars";
import { Pager } from "@/shared/components/Pager";
import { SearchSelect, type SearchSelectOption } from "@/shared/components/SearchSelect";
import { formatCompact, formatInteger } from "@/shared/utils/format";
import { formatMoney } from "@/shared/utils/format-currency";
import {
  INSTALLATION_TIME_ZONE,
  PERIOD_PRESET_LABELS,
  PERIOD_PRESETS,
  presetWindow,
  todayInInstallation,
  type DayWindow,
  type PeriodPreset
} from "@/shared/utils/installation-clock";
import {
  AI_STAGE_FAMILY_LABELS,
  aiStageFamily,
  aiStageLabel,
  type AiStageFamily
} from "@/shared/vocabulary/ai-stages";

import {
  aiCalls,
  callCostLabel,
  callModelLabel,
  callOwnerLabel,
  callOwnerTitle,
  costContext,
  costLabel,
  fillDays,
  groupLabel,
  optionLabel,
  modelLabel,
  OUTCOME_LABELS,
  OUTCOME_TONE,
  rankedItems,
  rankingBasis,
  rankingNoun,
  TIMELINE_METRIC_LABELS,
  TIMELINE_METRICS,
  timelineFormatter,
  timelinePoints,
  totalTokens
} from "./ai-usage-view";
import { AdminPageHeader } from "@/features/admin/AdminPageHeader";
import { Select } from "@/shared/components/Select";

const PAGE_SIZE = 50;

type Dimensions = { stage: string; model: string; company_id: string; user_id: string };
const SEM_DIMENSAO: Dimensions = { stage: "", model: "", company_id: "", user_id: "" };

function hasDimension(dims: Dimensions): boolean {
  return Object.values(dims).some(Boolean);
}

function toQuery(window: DayWindow, dims: Dimensions): AiUsageQuery {
  return {
    date_from: window.from,
    date_to: window.to,
    stage: dims.stage || undefined,
    model: dims.model || undefined,
    company_id: dims.company_id || undefined,
    user_id: dims.user_id || undefined
  };
}

const quandoFormatador = new Intl.DateTimeFormat("pt-BR", {
  timeZone: INSTALLATION_TIME_ZONE,
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit"
});

/** O carimbo da linha é UTC sem `Z`; a exibição é no fuso da
 *  INSTALAÇÃO — o mesmo em que o dia do filtro fecha, senão uma
 *  chamada das 23:30 apareceria no dia seguinte pra quem olha da
 *  Europa, fora do dia que a pessoa filtrou. */
function quando(recordedAt: string): string {
  const ms = parseServerTime(recordedAt);
  return ms === null ? recordedAt : quandoFormatador.format(new Date(ms));
}

function LinhaDaChamada({ call, currency }: { call: AiUsageCall; currency: string }) {
  const [aberta, setAberta] = useState(false);
  const temErro = call.error !== null && call.error !== "";
  return (
    <>
      <tr className={call.outcome === "ok" ? undefined : "ai-usage-row--muted"}>
        <td className="ai-usage-num">
          {temErro ? (
            <button
              className="audit-toggle"
              type="button"
              onClick={() => setAberta(!aberta)}
              aria-expanded={aberta}
              aria-label={`${aberta ? "Esconder" : "Ver"} o erro da chamada ${call.id}`}
            >
              {aberta ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              {quando(call.recorded_at)}
            </button>
          ) : (
            quando(call.recorded_at)
          )}
        </td>
        <td>{aiStageLabel(call.stage)}</td>
        <td className="ai-usage-owner" title={callOwnerTitle(call)}>{callOwnerLabel(call)}</td>
        <td className="ai-usage-model">
          {callModelLabel(call)}
          <small>{call.provider}</small>
        </td>
        <td className="ai-usage-num">
          {call.input_tokens === null ? "—" : formatInteger(call.input_tokens)}
        </td>
        <td className="ai-usage-num">
          {call.output_tokens === null ? "—" : formatInteger(call.output_tokens)}
        </td>
        <td className={`ai-usage-num${call.cost_status === "priced" ? "" : " ai-usage-cost-missing"}`}>
          {callCostLabel(call, currency)}
        </td>
        <td>
          <span className={`badge-pill ${OUTCOME_TONE[call.outcome]}`}>{OUTCOME_LABELS[call.outcome]}</span>
        </td>
      </tr>
      {aberta && temErro && (
        <tr className="audit-detail-row">
          <td colSpan={8}>
            <pre className="ai-usage-error">{call.error}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

function TabelaDeEtapas({ stages, currency }: { stages: AiUsageStageSlice[]; currency: string }) {
  const familias: AiStageFamily[] = ["pipeline", "personal"];
  return (
    <div className="ai-usage-table-wrap">
    <table className="ai-admin-table ai-usage-stages">
      <thead>
        <tr>
          <th>Etapa</th>
          <th className="ai-usage-num">Chamadas</th>
          <th className="ai-usage-num">Tokens</th>
          <th className="ai-usage-num">Custo</th>
        </tr>
      </thead>
      {familias.map(familia => (
        <tbody key={familia}>
          <tr className="ai-usage-family">
            <th colSpan={4} scope="rowgroup">{AI_STAGE_FAMILY_LABELS[familia]}</th>
          </tr>
          {stages
            .filter(stage => stage.family === familia)
            .map(stage => {
              const tokens = totalTokens(stage);
              return (
                <tr key={stage.key}>
                  <td>
                    {aiStageLabel(stage.key)}
                    {stage.key === "copiloto" && stage.calls > 0 && (
                      <small className="ai-usage-note" title="A busca na web da Mestra Help tem custo por requisição que o provedor não informa junto dos tokens. O que aparece aqui é só o custo dos tokens.">
                        + busca não apurada
                      </small>
                    )}
                  </td>
                  <td className="ai-usage-num">{formatInteger(aiCalls(stage))}</td>
                  <td className="ai-usage-num">{tokens === null ? "—" : formatCompact(tokens)}</td>
                  <td className={`ai-usage-num${stage.cost === null && aiCalls(stage) > 0 ? " ai-usage-cost-missing" : ""}`}>
                    {aiCalls(stage) === 0 ? "—" : costLabel(stage.cost, currency)}
                  </td>
                </tr>
              );
            })}
        </tbody>
      ))}
    </table>
    </div>
  );
}

export function AiUsagePage({ token }: { token: string }) {
  const [preset, setPreset] = useState<PeriodPreset>("last30");
  const [custom, setCustom] = useState<DayWindow>(() => presetWindow("last30"));
  const [dims, setDims] = useState<Dimensions>(SEM_DIMENSAO);
  const [page, setPage] = useState(1);

  const window = useMemo<DayWindow>(
    () => (preset === "custom" ? custom : presetWindow(preset)),
    [preset, custom]
  );
  // O usuário pode digitar o fim antes do início; o servidor troca os
  // dois, e a tela manda como está pra não brigar com quem digita.
  const janelaValida = /^\d{4}-\d{2}-\d{2}$/.test(window.from) && /^\d{4}-\d{2}-\d{2}$/.test(window.to);

  const [base, setBase] = useState<AiUsageSummary | null>(null);
  const [filtered, setFiltered] = useState<AiUsageSummary | null>(null);
  const [calls, setCalls] = useState<AiUsageCallsPage | null>(null);
  // Um erro POR consulta (2026-10-09, teste de render): com um só, a
  // consulta que dava certo não sabia limpar a falha da outra — o banner
  // vermelho ficava com tudo já carregado — e a que limpava (o resumo
  // base) apagava a falha das demais. Cada uma só mexe no seu.
  const [erroBase, setErroBase] = useState<string | null>(null);
  const [erroFiltrado, setErroFiltrado] = useState<string | null>(null);
  const [erroChamadas, setErroChamadas] = useState<string | null>(null);
  const erro = erroBase ?? erroFiltrado ?? erroChamadas;
  const [carregando, setCarregando] = useState(true);
  const [tentativa, setTentativa] = useState(0);

  // O resumo SEM dimensão — de onde saem as opções dos filtros.
  useEffect(() => {
    if (!janelaValida) return;
    let vivo = true;
    setCarregando(true);
    apiClient
      .getAiUsageSummary(token, toQuery(window, SEM_DIMENSAO))
      .then(resumo => {
        if (!vivo) return;
        setBase(resumo);
        setErroBase(null);
      })
      .catch(error => {
        if (vivo) setErroBase(errorMessage(error, "Não foi possível carregar o consumo."));
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, [token, window, janelaValida, tentativa]);

  // O resumo COM dimensão — só quando há alguma; senão é o mesmo de cima.
  const filtrando = hasDimension(dims);
  useEffect(() => {
    if (!janelaValida || !filtrando) {
      setFiltered(null);
      setErroFiltrado(null);
      return;
    }
    let vivo = true;
    apiClient
      .getAiUsageSummary(token, toQuery(window, dims))
      .then(resumo => {
        if (!vivo) return;
        setFiltered(resumo);
        setErroFiltrado(null);
      })
      .catch(error => {
        if (vivo) setErroFiltrado(errorMessage(error, "Não foi possível aplicar o filtro."));
      });
    return () => {
      vivo = false;
    };
  }, [token, window, janelaValida, dims, filtrando, tentativa]);

  useEffect(() => {
    if (!janelaValida) return;
    let vivo = true;
    apiClient
      .listAiUsageCalls(token, { ...toQuery(window, dims), page, size: PAGE_SIZE })
      .then(pagina => {
        if (!vivo) return;
        setCalls(pagina);
        setErroChamadas(null);
      })
      .catch(error => {
        if (vivo) setErroChamadas(errorMessage(error, "Não foi possível listar as chamadas."));
      });
    return () => {
      vivo = false;
    };
  }, [token, window, janelaValida, dims, page, tentativa]);

  const summary = filtrando ? filtered : base;
  const currency = summary?.currency ?? "USD";
  const fusoDiverge = summary !== null && summary.timezone !== INSTALLATION_TIME_ZONE;

  function mudarDimensao(patch: Partial<Dimensions>) {
    setDims(atual => ({ ...atual, ...patch }));
    setPage(1);
  }

  function escolherPreset(novo: PeriodPreset) {
    if (novo === "custom" && preset !== "custom") setCustom(window);
    setPreset(novo);
    setPage(1);
  }

  // A busca das caixas de Empresa e Pessoa: quem digita alcança quem
  // ficou fora das 50 do ranking. `useCallback` na janela, senão a
  // caixa refaria a busca a cada render.
  const buscarEmpresa = useCallback(
    (q: string): Promise<SearchSelectOption[]> =>
      apiClient
        .searchAiUsageOptions(token, { kind: "company", q, date_from: window.from, date_to: window.to })
        .then(pagina => pagina.items.map(item => ({ value: item.key, label: item.label }))),
    [token, window]
  );
  const buscarPessoa = useCallback(
    (q: string): Promise<SearchSelectOption[]> =>
      apiClient
        .searchAiUsageOptions(token, { kind: "user", q, date_from: window.from, date_to: window.to })
        .then(pagina => pagina.items.map(item => ({ value: item.key, label: optionLabel(item) }))),
    [token, window]
  );
  const opcoesDeEmpresa = useMemo(
    () => (base?.companies ?? []).map(empresa => ({ value: empresa.key, label: groupLabel(empresa) })),
    [base]
  );
  const opcoesDePessoa = useMemo(
    () => (base?.users ?? []).map(pessoa => ({ value: pessoa.key, label: groupLabel(pessoa) })),
    [base]
  );

  const modelosRanking = summary ? rankingBasis(summary.models) : "calls";
  const empresasRanking = summary ? rankingBasis(summary.companies) : "calls";
  const pessoasRanking = summary ? rankingBasis(summary.users) : "calls";

  return (
    <div className="ai-usage-page ajustes-panel admin-panel">
      <AdminPageHeader
        title="Consumo de IA"
        description="O que a instalação gastou com os provedores, em dólar, pelo preço da época de cada chamada. O dia fecha em Brasília."
      />

      <div className="card ai-usage-filters">
        <fieldset className="ai-usage-presets">
          <legend>Período</legend>
          {PERIOD_PRESETS.map(opcao => (
            <label key={opcao} className={`ai-usage-preset${preset === opcao ? " is-active" : ""}`}>
              <input
                type="radio"
                name="ai-usage-period"
                value={opcao}
                checked={preset === opcao}
                onChange={() => escolherPreset(opcao)}
              />
              <span>{PERIOD_PRESET_LABELS[opcao]}</span>
            </label>
          ))}
        </fieldset>
        <div className="ai-usage-dates">
          <label className="field">
            <span>De</span>
            <input
              type="date"
              value={window.from}
              max={todayInInstallation()}
              onChange={event => {
                setCustom({ ...window, from: event.target.value });
                setPreset("custom");
                setPage(1);
              }}
            />
          </label>
          <label className="field">
            <span>Até</span>
            <input
              type="date"
              value={window.to}
              max={todayInInstallation()}
              onChange={event => {
                setCustom({ ...window, to: event.target.value });
                setPreset("custom");
                setPage(1);
              }}
            />
          </label>
        </div>
        <div className="ai-usage-dimensions">
          <label className="field">
            <span>Etapa</span>
            <Select value={dims.stage} onChange={event => mudarDimensao({ stage: event.target.value })}>
              <option value="">Todas</option>
              {(["pipeline", "personal"] as AiStageFamily[]).map(familia => (
                <optgroup key={familia} label={AI_STAGE_FAMILY_LABELS[familia]}>
                  {(base?.stages ?? [])
                    .filter(stage => aiStageFamily(stage.key) === familia)
                    .map(stage => (
                      <option key={stage.key} value={stage.key}>
                        {aiStageLabel(stage.key)}
                      </option>
                    ))}
                </optgroup>
              ))}
            </Select>
          </label>
          <label className="field">
            <span>Modelo</span>
            <Select value={dims.model} onChange={event => mudarDimensao({ model: event.target.value })}>
              <option value="">Todos</option>
              {(base?.models ?? []).map(modelo => (
                <option key={modelo.key} value={modelo.key}>
                  {modelLabel(modelo)}
                </option>
              ))}
            </Select>
          </label>
          <SearchSelect
            label="Empresa"
            value={dims.company_id}
            options={opcoesDeEmpresa}
            search={buscarEmpresa}
            onChange={company_id => mudarDimensao({ company_id })}
            disabled={Boolean(dims.user_id)}
            disabledReason="Empresa e pessoa não se combinam: a esteira é da empresa, a Mestra Help é da pessoa."
          />
          <SearchSelect
            label="Pessoa"
            value={dims.user_id}
            options={opcoesDePessoa}
            search={buscarPessoa}
            onChange={user_id => mudarDimensao({ user_id })}
            disabled={Boolean(dims.company_id)}
            disabledReason="Empresa e pessoa não se combinam: a esteira é da empresa, a Mestra Help é da pessoa."
          />
          {filtrando && (
            <button type="button" className="ghost-button ai-usage-clear" onClick={() => mudarDimensao(SEM_DIMENSAO)}>
              <SlidersHorizontal size={14} aria-hidden="true" />
              Limpar filtros
            </button>
          )}
        </div>
      </div>

      {fusoDiverge && summary && (
        <div className="cena-warnings ai-usage-warning" role="alert">
          <p className="cena-warnings-title">
            <AlertTriangle size={16} aria-hidden="true" />
            O servidor fecha o dia em {summary.timezone}, e esta tela conta em {INSTALLATION_TIME_ZONE}.
          </p>
          <p>Os atalhos de período podem pedir um dia a mais ou a menos até os dois fusos serem o mesmo.</p>
        </div>
      )}

      {erro && (
        <ApiState kind="error" onRetry={() => setTentativa(n => n + 1)}>
          {erro}
        </ApiState>
      )}

      {carregando && !summary ? (
        <ApiState kind="loading">Carregando o consumo…</ApiState>
      ) : summary && (
        <>
          <section className="dashboard-kpi-grid" aria-label="Totais do período">
            <MetricCard
              icon={<Coins size={18} />}
              label="Custo apurado"
              value={costLabel(summary.totals.cost, currency)}
              context={costContext(summary.totals)}
              tone="card1"
              title={summary.totals.cost === null ? undefined : formatMoney(summary.totals.cost, currency, 6)}
            />
            <MetricCard
              icon={<Cpu size={18} />}
              label="Chamadas de IA"
              value={formatInteger(aiCalls(summary.totals))}
              context={
                summary.totals.failed + summary.totals.unfinished > 0
                  ? `${formatInteger(summary.totals.failed)} falharam · ${formatInteger(summary.totals.unfinished)} não terminaram`
                  : "todas concluídas"
              }
              tone="card2"
            />
            <MetricCard
              icon={<Hash size={18} />}
              label="Tokens de entrada"
              value={summary.totals.input_tokens === null ? "—" : formatCompact(summary.totals.input_tokens)}
              context={
                summary.totals.tokenless_calls > 0
                  ? `${formatInteger(summary.totals.tokenless_calls)} chamadas sem token registrado`
                  : "prompt, histórico e instruções"
              }
              tone="card3"
              title={summary.totals.input_tokens === null ? undefined : formatInteger(summary.totals.input_tokens)}
            />
            <MetricCard
              icon={<Hash size={18} />}
              label="Tokens de saída"
              value={summary.totals.output_tokens === null ? "—" : formatCompact(summary.totals.output_tokens)}
              context="resposta, incluindo o raciocínio do modelo"
              title={summary.totals.output_tokens === null ? undefined : formatInteger(summary.totals.output_tokens)}
            />
            <MetricCard
              icon={<ListChecks size={18} />}
              label="Resolvidas sem IA"
              value={formatInteger(summary.totals.resolved_without_ai)}
              context="o determinismo decidiu antes de chamar"
            />
            <MetricCard
              icon={<MessageSquareOff size={18} />}
              label="Não chamou por configuração"
              value={formatInteger(summary.totals.not_called_by_config)}
              context="etapa desligada ou sem modelo pra tarefa"
            />
          </section>

          <section className="dashboard-section-grid">
            {/* A tabela por etapa é a leitura principal (a dona, 2026-10-21):
                fica larga, à esquerda; o gráfico de evolução vai estreito, à
                direita. */}
            <Panel title="Por etapa" subtitle="A esteira e as chamadas por pessoa" className="dashboard-span-8">
              <TabelaDeEtapas stages={summary.stages} currency={currency} />
            </Panel>
            {/* As três métricas empilhadas, em vez de um seletor (a dona,
                2026-10-22): no painel estreito cabem três gráficos baixos, e
                ninguém precisa clicar pra comparar custo, chamadas e tokens. */}
            <Panel
              title="Evolução no período"
              subtitle="Dia a dia, no fuso da instalação"
              className="dashboard-span-4 ai-usage-evolution"
            >
              <div className="ai-usage-timelines">
                {TIMELINE_METRICS.map(opcao => (
                  <section key={opcao} className="ai-usage-timeline" aria-label={TIMELINE_METRIC_LABELS[opcao]}>
                    <h4>{TIMELINE_METRIC_LABELS[opcao]}</h4>
                    {/* Mais quadrado que o padrão (240 contra 150 de altura na
                        caixa de 720), sem passar disso: a dona achou o de 300
                        grande demais. Os três dividem a altura do painel
                        vizinho, centrados na própria fatia. */}
                    <LineChart
                      variant="minimal"
                      height={240}
                      points={timelinePoints(fillDays(summary.timeline, summary.date_from, summary.date_to), opcao)}
                      formatValue={timelineFormatter(opcao, currency)}
                    />
                  </section>
                ))}
              </div>
            </Panel>
            <Panel
              title="Por modelo"
              subtitle={modelosRanking === "cost" ? "Custo apurado por modelo pedido" : "Chamadas por modelo (sem custo apurado no período)"}
              className="dashboard-span-6"
            >
              {summary.models.length === 0 ? (
                <EmptyState label="Nenhuma chamada de IA no período" />
              ) : (
                <>
                  <RankedBars
                    items={rankedItems(summary.models, modelosRanking, modelLabel)}
                    noun={rankingNoun(modelosRanking)}
                    limit={10}
                    formatValue={modelosRanking === "cost" ? value => formatMoney(value, currency) : formatCompact}
                  />
                  <ul className="ai-usage-model-notes">
                    {summary.models.map(modelo => (
                      <li key={modelo.key}>
                        <strong>{modelLabel(modelo)}</strong>
                        <span> · {modelo.provider}</span>
                        {modelo.catalog_name === null && <span> · fora do cadastro</span>}
                        {modelo.unpriced_calls > 0 && (
                          <span className="ai-usage-cost-missing"> · {formatInteger(modelo.unpriced_calls)} sem preço</span>
                        )}
                        {modelo.responded_names.some(nome => nome.toLowerCase() !== modelo.key) && (
                          <span className="ai-usage-responded"> · respondeu como {modelo.responded_names.join(", ")}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Panel>
            {!filtrando || (!dims.company_id && !dims.user_id) ? (
              <>
                <Panel
                  title="Por empresa"
                  subtitle={empresasRanking === "cost" ? "Custo apurado da esteira, por cliente" : "Chamadas da esteira, por cliente"}
                  className="dashboard-span-6"
                >
                  <RankedBars
                    items={rankedItems(summary.companies, empresasRanking, groupLabel)}
                    noun={rankingNoun(empresasRanking)}
                    limit={10}
                    formatValue={empresasRanking === "cost" ? value => formatMoney(value, currency) : formatCompact}
                  />
                </Panel>
                <Panel
                  title="Por pessoa"
                  subtitle={pessoasRanking === "cost" ? "Custo apurado da Mestra Help e do autofill, por pessoa" : "Chamadas da Mestra Help e do autofill, por pessoa"}
                  className="dashboard-span-6"
                >
                  <RankedBars
                    items={rankedItems(summary.users, pessoasRanking, groupLabel)}
                    noun={rankingNoun(pessoasRanking)}
                    limit={10}
                    formatValue={pessoasRanking === "cost" ? value => formatMoney(value, currency) : formatCompact}
                  />
                </Panel>
              </>
            ) : null}
          </section>

          <div className="card">
            <div className="card-title">
              <div>
                <ListChecks size={18} aria-hidden="true" />
                <h3>Chamadas</h3>
                <p>{calls ? `${formatInteger(calls.total)} linha${calls.total === 1 ? "" : "s"} no recorte, da mais recente pra mais antiga` : "Carregando…"}</p>
              </div>
            </div>
            {calls && calls.items.length === 0 ? (
              <p className="copiloto-empty-list">Nenhuma linha com esse recorte.</p>
            ) : (
              <div className="ai-usage-table-wrap">
                <table className="ai-admin-table ai-usage-calls">
                  <thead>
                    <tr>
                      <th>Quando</th>
                      <th>Etapa</th>
                      <th>Empresa ou pessoa</th>
                      <th>Modelo</th>
                      <th className="ai-usage-num">Entrada</th>
                      <th className="ai-usage-num">Saída</th>
                      <th className="ai-usage-num">Custo</th>
                      <th>Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calls?.items.map(call => (
                      <LinhaDaChamada key={call.id} call={call} currency={currency} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {calls && (
              <Pager page={calls.page} size={calls.size} count={{ total: calls.total }} onPage={setPage} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
